// React-query hooks for the raw-profile uploads surface.
//
// The page-level component is presentation-only; all server-state
// concerns (fetching, mutations, toasts, invalidation keys) live here
// so they can be reused and tested in isolation.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import type { BundlesResponse } from "./types";

const BUNDLES_QUERY_KEY = ["bundles"] as const;

interface ReinspectAllResult {
	success?: boolean;
	updated?: number;
	failed?: number;
	total?: number;
	error?: { message?: string };
}

interface MutationResult {
	success?: boolean;
	error?: { message?: string };
}

async function postJson(path: string): Promise<MutationResult> {
	const res = await fetch(path, {
		method: "POST",
		credentials: "include",
	});
	const data = (await res.json()) as MutationResult;
	if (!res.ok || !data.success) {
		throw new Error(data.error?.message ?? `HTTP ${res.status}`);
	}
	return data;
}

async function deleteJson(path: string): Promise<MutationResult> {
	const res = await fetch(path, {
		method: "DELETE",
		credentials: "include",
	});
	const data = (await res.json()) as MutationResult;
	if (!res.ok || !data.success) {
		throw new Error(data.error?.message ?? `HTTP ${res.status}`);
	}
	return data;
}

export function useBundles() {
	return useQuery({
		queryKey: BUNDLES_QUERY_KEY,
		queryFn: () =>
			unwrap(api.api.upload["raw-profiles"].get()) as Promise<BundlesResponse>,
	});
}

export function useReinspectAll() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (): Promise<ReinspectAllResult> => {
			const res = await fetch("/api/upload/raw-profiles/reinspect-all", {
				method: "POST",
				credentials: "include",
			});
			const data = (await res.json()) as ReinspectAllResult;
			if (!res.ok || !data.success) {
				throw new Error(data.error?.message ?? `HTTP ${res.status}`);
			}
			return data;
		},
		onSuccess: (data) => {
			toast.success(
				`Re-inspected ${data.updated}/${data.total} bundles (${data.failed} failed)`,
			);
			queryClient.invalidateQueries({ queryKey: BUNDLES_QUERY_KEY });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Re-inspect failed"),
	});
}

export function useReinspectOne() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			postJson(`/api/upload/raw-profiles/${encodeURIComponent(id)}/reinspect`),
		onSuccess: () => {
			toast.success("Re-inspected");
			queryClient.invalidateQueries({ queryKey: BUNDLES_QUERY_KEY });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Re-inspect failed"),
	});
}

export function useDeleteUpload(opts?: { onDeleted?: () => void }) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			deleteJson(`/api/upload/raw-profiles/${encodeURIComponent(id)}`),
		onSuccess: () => {
			toast.success("Upload deleted");
			queryClient.invalidateQueries({ queryKey: BUNDLES_QUERY_KEY });
			opts?.onDeleted?.();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});
}
